/**
 * Holly anonymization helpers.
 *
 * The Holly API returns warehouse names and descriptions that can identify
 * specific facilities (e.g. "Heathrow Bonded Warehouse",
 * "Bonded storage + customs clearance, Heathrow, UB7"). The website should
 * never display the real name and only show a generic locality (the town /
 * city / area), never the full address or postcode.
 */

/* UK-style postcode token, e.g. NW10, UB7, RM10, EC1A, B11. */
const UK_POSTCODE = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d?[A-Z]{0,2}$/i;

/**
 * Extract just the town / locality from a Holly warehouse description.
 *
 * Strategy: split on commas, trim, drop trailing chunks that are pure
 * postcodes, then take the last remaining chunk and strip any trailing
 * postcode-like word from it. Returns null if nothing useful is left.
 *
 * Examples:
 *   "Pallet storage, fulfilment, cross-dock — Park Royal, London NW10"
 *     → "London"
 *   "Ambient + temperature-controlled storage, Dagenham, RM10"
 *     → "Dagenham"
 *   "Bonded storage + customs clearance, Heathrow, UB7"
 *     → "Heathrow"
 */
export function extractTownFromDescription(
  description: string | null | undefined
): string | null {
  if (!description) return null;

  const parts = description
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  if (parts.length === 0) return null;

  /* Drop trailing chunks that are nothing but a postcode. */
  while (parts.length > 0 && UK_POSTCODE.test(parts[parts.length - 1])) {
    parts.pop();
  }
  if (parts.length === 0) return null;

  /* From the last remaining chunk, strip a trailing postcode-like word. */
  const tokens = parts[parts.length - 1].split(/\s+/).filter(Boolean);
  while (tokens.length > 1 && UK_POSTCODE.test(tokens[tokens.length - 1])) {
    tokens.pop();
  }

  const town = tokens.join(' ').trim();
  return town.length > 0 ? town : null;
}
